/**
 * The street «Ищем водителя» stands on — `search_street.dart` in the phone app, drawn
 * here on a canvas from the same numbers.
 *
 * The other ride screens stand on finished paintings, and there is no painting of
 * this moment: nobody has taken the ride yet, so there is no car of ours to paint.
 * What there is to show is the passenger at the kerb with the order in their hand,
 * and the town's cars going past. So this one is drawn rather than painted, in the
 * paintings' palette and on their plan — the town behind, trees in front of it, the
 * road, and bushes at the foot — so that it reads as the same street a minute
 * earlier, and the change to «Заказ принят» is the car arriving rather than the
 * screen changing style. Our cars are the paintings' white saloons with the mark on
 * the door, and nothing on the roof.
 *
 * Each stage of the search is its own scene, so that ten minutes are not one loop:
 *
 * - `offering` — the order goes to one driver at a time: as one of our cars comes up
 *   the road, a pin with the mark drops onto its roof and rides along on it until
 *   the car is past.
 * - `open` — everybody can see it: the rings go out wide over the road, and the
 *   traffic thickens.
 * - `continuing` — still looking: the passenger walks up and down the kerb, the
 *   phone in hand.
 * - `longer` — it is taking a while: the passenger has sat down on the bench, and the
 *   cat has gone to sleep beside it.
 * - `lastMinute` — the passenger is up again at the kerb, watching the road, and the
 *   cat is awake and sat up on the bench watching it with them. Over the passenger
 *   hangs the pin from the first stage, in the amber the words over it are written
 *   in, its ring emptying with the minute; and the town's cars stop coming round one
 *   by one, until the road is empty for the last twenty seconds.
 *
 * Everything is laid out on the paintings' own 1050 × 591, and the box crops it from
 * the middle the way `object-fit: cover` crops them: the passenger stands just right
 * of centre, where a narrow phone keeps them.
 */

import type { SearchStage } from './search';

export type Sky = 'day' | 'dark' | 'night';

export interface SceneOptions {
  sky: Sky;
  stage: SearchStage;
  /**
   * The second of the street's own clock at which the search gives up; null where
   * nothing will. The last minute is drawn from it: the pin's ring, and which cars
   * still come round.
   */
  end?: number | null;
}

/**
 * The moment drawn for anybody who has asked for no animations: in every stage it
 * catches the scene mid-gesture — the pin coming down onto a car, the passenger mid-step,
 * the hand up — so that the still picture still says what is happening.
 */
export const STILL = 1.35;

/** The paintings' own size, the plan everything here is placed on. */
export const W = 1050;
export const H = 591;

/** Where the road and the pavement lie, top to bottom. */
const HORIZON = 372;
const FAR_LANE = 420;
const NEAR_LANE = 466;
const KERB = 474;
const FEET = 526;

/** The amber the last minute is written in, and the same deepened for the mark on the pin. */
const AMBER = '#FFD07A';
const AMBER_DEEP = '#E09A2E';

/** How long the last stage is: the search's last minute, in seconds. */
const MINUTE = 60;

/** Where the passenger stands, and where the bench is that they sit down on. */
const PASSENGER = 610;
const BENCH = 405;
const SEAT = 462;

/**
 * The service's mark, as `assets/logo.svg` draws it on a 100 × 55.59 box — copied
 * here, and checked against the file by `street-scene.spec.ts`.
 */
export const MARK =
  'M19.88 55.38C20.7 55.22 21.87 54.84 22.46 54.54'
  + 'C22.64 54.44 23.01 54.29 23.27 54.19C24.3 53.8 25.3 53.1 26.42 51.99'
  + 'C27.51 50.91 28.44 49.64 28.44 49.21C28.44 49.16 28.57 48.83 28.73 48.48'
  + 'C29.11 47.63 29.24 46.93 29.24 45.67C29.24 44.34 29.11 42.16 28.98 41.25'
  + 'C28.38 37.05 28.51 33.44 29.35 30.86L29.61 30.1L30.3 29.46'
  + 'C30.68 29.1 31.25 28.62 31.55 28.38C31.86 28.14 32.32 27.77 32.57 27.56'
  + 'C33.07 27.13 33.1 27.12 34.37 26.66C35.67 26.19 36.55 26.07 38.45 26.08'
  + 'C40.1 26.09 40.1 26.09 41.94 26.64C43.12 26.99 44.12 27.42 44.34 27.68'
  + 'C45.11 28.56 45.3 28.81 45.6 29.3C45.78 29.6 45.96 29.93 46.0 30.03'
  + 'C46.14 30.4 46.45 32.46 46.81 35.36C46.85 35.72 46.87 37.28 46.86 39.22'
  + 'L46.84 42.48L47.15 43.56C47.46 44.67 47.52 44.81 48.16 45.98'
  + 'C48.71 46.99 49.91 48.2 51.56 49.39C52.1 49.78 53.77 50.55 54.89 50.94'
  + 'L56.05 51.33L57.51 51.41C59.7 51.54 61.69 51.42 62.54 51.11'
  + 'C62.89 50.99 63.45 50.8 63.79 50.69C64.13 50.58 64.53 50.4 64.7 50.29'
  + 'C64.86 50.19 65.25 49.99 65.56 49.86C66.47 49.48 66.93 49.14 67.98 48.08'
  + 'C68.52 47.54 69.07 47.01 69.22 46.9C69.52 46.68 70.08 45.62 70.61 44.25'
  + 'C70.99 43.26 71.05 42.97 71.2 41.21C71.38 39.02 71.44 38.56 71.73 37.15'
  + 'C71.98 35.89 72.02 35.78 72.32 35.31C73.25 33.87 73.5 33.57 74.21 33.07'
  + 'C75.22 32.37 75.85 32.19 77.63 32.13C79.11 32.07 79.49 32.17 81.39 33.11'
  + 'C82.46 33.65 83.02 33.88 83.56 34.02C83.95 34.13 84.52 34.3 84.82 34.4'
  + 'L85.36 34.59L87.71 34.59L90.06 34.59L90.53 34.39'
  + 'C90.79 34.28 91.35 34.11 91.78 34.02C92.65 33.83 94.08 33.14 95.01 32.48'
  + 'C95.27 32.29 95.63 32.05 95.81 31.93C97.1 31.1 98.77 28.99 99.17 27.69'
  + 'C99.23 27.47 99.41 26.97 99.57 26.57C99.92 25.67 100.0 25.16 100.0 23.97'
  + 'C99.99 22.56 99.9 21.65 99.71 21.22C99.62 21.03 99.46 20.61 99.34 20.29'
  + 'C99.23 19.98 99.04 19.57 98.93 19.39C98.83 19.21 98.64 18.86 98.52 18.62'
  + 'C97.81 17.21 95.48 15.1 93.57 14.14C93.0 13.86 90.42 13.12 89.65 13.02'
  + 'C86.33 12.61 83.88 13.1 80.81 14.8C79.4 15.58 79.37 15.6 78.71 16.31'
  + 'C78.34 16.71 77.93 17.18 77.81 17.36C77.68 17.54 77.42 17.85 77.23 18.04'
  + 'C76.69 18.62 76.25 19.54 75.5 21.66C75.39 21.98 75.18 22.48 75.04 22.78'
  + 'C74.89 23.07 74.77 23.35 74.77 23.4C74.77 23.44 74.66 23.67 74.51 23.9'
  + 'C74.37 24.14 74.21 24.45 74.16 24.6C74.1 24.75 73.94 25.05 73.8 25.28'
  + 'C73.66 25.5 73.47 25.86 73.39 26.08C73.29 26.35 73.09 26.64 72.79 26.96'
  + 'C72.54 27.22 72.23 27.6 72.1 27.79C71.97 27.98 71.72 28.28 71.54 28.44'
  + 'C71.36 28.61 71.04 28.89 70.84 29.08C70.41 29.47 68.77 30.34 68.23 30.45'
  + 'C68.03 30.5 67.7 30.63 67.49 30.75C66.8 31.14 63.06 31.48 61.15 31.33'
  + 'C58.65 31.13 56.56 30.69 55.6 30.16C55.31 30.01 54.88 29.81 54.64 29.73'
  + 'C53.79 29.46 52.07 28.43 51.29 27.73C51.11 27.57 50.82 27.33 50.65 27.2'
  + 'C50.34 26.96 49.35 25.62 49.02 25.0C48.74 24.48 48.66 23.94 48.7 22.96'
  + 'C48.72 22.34 48.78 21.98 48.88 21.69C48.96 21.48 49.13 21.02 49.25 20.68'
  + 'C49.38 20.34 49.59 19.82 49.73 19.53C49.91 19.13 50.01 18.77 50.09 18.19'
  + 'C50.16 17.75 50.27 16.98 50.35 16.48C50.53 15.29 50.53 15.06 50.27 13.32'
  + 'C50.05 11.75 50.05 11.77 49.41 10.68C48.15 8.54 47.06 7.38 44.99 5.99'
  + 'C43.98 5.31 43.93 5.28 43.32 5.18C42.98 5.12 42.33 4.96 41.88 4.82'
  + 'C40.76 4.46 40.21 4.4 38.45 4.4C36.75 4.4 36.29 4.46 35.09 4.82'
  + 'C34.64 4.96 34.03 5.1 33.76 5.15C33.25 5.22 33.17 5.26 32.16 5.91'
  + 'C31.89 6.08 31.44 6.36 31.18 6.51C30.51 6.91 28.95 8.48 28.43 9.26'
  + 'C27.93 10.02 27.19 11.6 27.03 12.23C26.74 13.38 26.82 16.54 27.18 18.15'
  + 'C27.73 20.64 27.8 24.3 27.33 25.85C27.23 26.17 27.08 26.76 27.0 27.18'
  + 'C26.91 27.63 26.75 28.16 26.58 28.55C26.42 28.89 26.27 29.24 26.25 29.31'
  + 'C26.0 30.03 24.31 31.71 23.35 32.19C22.19 32.78 20.03 32.83 18.86 32.29'
  + 'C18.34 32.05 17.39 31.14 17.1 30.59C16.97 30.35 16.76 30.01 16.64 29.84'
  + 'C16.3 29.4 16.3 29.4 16.12 28.72C16.03 28.37 15.86 27.82 15.76 27.48'
  + 'C15.57 26.89 15.57 26.81 15.6 25.74C15.62 25.04 15.68 24.47 15.75 24.22'
  + 'C15.82 24.01 15.95 23.5 16.05 23.1C16.31 22.11 17.3 20.1 17.84 19.48'
  + 'C18.06 19.23 18.3 18.88 18.39 18.7C18.48 18.52 18.68 18.27 18.83 18.15'
  + 'C19.33 17.75 20.97 15.89 21.39 15.23C21.93 14.4 22.42 13.31 22.55 12.68'
  + 'C22.81 11.42 22.81 9.15 22.55 7.67C22.43 6.98 22.22 6.5 21.61 5.54'
  + 'C21.49 5.34 21.28 5.0 21.14 4.77C20.7 4.02 18.2 1.87 17.32 1.48'
  + 'C17.1 1.38 16.77 1.21 16.59 1.09C15.99 0.71 13.55 0.17 12.03 0.08'
  + 'C10.7 0.0 10.19 0.04 9.04 0.34C8.52 0.47 7.79 0.63 7.41 0.7'
  + 'C7.04 0.76 6.62 0.86 6.48 0.92C5.94 1.14 4.16 2.27 3.73 2.65'
  + 'C3.48 2.87 3.2 3.1 3.1 3.17C2.7 3.43 1.41 5.08 0.91 5.97'
  + 'C0.4 6.89 0.36 7.01 0.17 8.0C0.03 8.8 0.0 9.16 0.0 10.46L0.0 11.97L0.49 13.42'
  + 'C0.81 14.37 1.05 14.98 1.21 15.2C1.84 16.09 2.29 16.63 2.64 16.92'
  + 'C2.86 17.1 3.18 17.37 3.35 17.53C3.52 17.7 3.91 17.98 4.21 18.17'
  + 'C4.52 18.35 5.06 18.68 5.42 18.9C6.51 19.57 8.16 21.32 8.69 22.38'
  + 'C8.79 22.58 9.0 22.92 9.16 23.13C9.32 23.35 9.55 23.77 9.67 24.07'
  + 'C9.78 24.37 9.97 24.79 10.09 24.99C10.2 25.19 10.39 25.71 10.51 26.15'
  + 'C10.63 26.58 10.82 27.21 10.92 27.55C11.11 28.12 11.12 28.25 11.12 29.46'
  + 'C11.12 31.01 11.03 31.75 10.7 32.85C10.41 33.81 9.59 35.48 9.09 36.14'
  + 'C7.36 38.43 6.72 39.38 6.23 40.38C6.12 40.62 5.9 40.99 5.76 41.2'
  + 'C5.52 41.56 5.49 41.65 5.37 42.61C5.14 44.48 5.23 46.4 5.6 47.51'
  + 'C6.03 48.8 7.27 51.0 7.88 51.55C7.99 51.65 8.22 51.92 8.4 52.14'
  + 'C8.61 52.41 8.84 52.62 9.07 52.74C9.26 52.84 9.54 53.06 9.68 53.23'
  + 'C10.11 53.76 12.34 54.67 15.0 55.39C15.75 55.59 15.78 55.59 17.42 55.57'
  + 'C18.78 55.55 19.23 55.51 19.88 55.38Z';

interface Palette {
  skyTop: string;
  skyLow: string;
  cloud: string;
  farTowers: string;
  towers: string;
  window: string;
  lit: string;
  trees: [string, string, string];
  trunk: string;
  farKerb: string;
  road: string;
  dash: string;
  kerb: string;
  pavement: string;
  joint: string;
  bushes: [string, string, string];
  body: string;
  bodyShade: string;
  glass: string;
  tyre: string;
  rim: string;
  logo: string;
  /** The pin the order drops onto our car with: the mark's blue, lit up at night. */
  pin: string;
  skin: string;
  hair: string;
  coat: string;
  coatShade: string;
  trousers: string;
  bag: string;
  bagStrap: string;
  shoe: string;
  cat: string;
  catLight: string;
  pole: string;
  wood: string;
  shadow: string;
}

const palettes: Record<Sky, Palette> = {
  day: {
    skyTop: '#4BB6FD',
    skyLow: '#B4E1FE',
    cloud: '#FFFFFF',
    farTowers: '#9CC8F4',
    towers: '#3C92EE',
    window: '#BFE0FF',
    lit: '#BFE0FF',
    trees: ['#1F8C47', '#2AA452', '#46BC62'],
    trunk: '#6E4B3A',
    farKerb: '#CFDDF0',
    road: '#5A73A6',
    dash: '#E8EEF8',
    kerb: '#D7E2F6',
    pavement: '#EDE6EA',
    joint: '#D8D0D8',
    bushes: ['#1E8A44', '#2FA34F', '#4CC266'],
    body: '#FBFCFE',
    bodyShade: '#D9E1EC',
    glass: '#2C4A78',
    tyre: '#2A3142',
    rim: '#B8C3D3',
    logo: '#03A1EA',
    pin: '#03A1EA',
    skin: '#F6A884',
    hair: '#3A2633',
    coat: '#E7705A',
    coatShade: '#C95A48',
    trousers: '#27538F',
    bag: '#F2B544',
    bagStrap: '#B9832A',
    shoe: '#232A3D',
    cat: '#F0A35E',
    catLight: '#FFF4E8',
    pole: '#4A5B7A',
    wood: '#B9774F',
    shadow: 'rgb(20 30 60 / 20%)',
  },
  dark: {
    skyTop: '#2B7FCB',
    skyLow: '#5DA2E2',
    cloud: '#D4E6FA',
    farTowers: '#5596D8',
    towers: '#1D5FA8',
    window: '#7FB3E6',
    lit: '#7FB3E6',
    trees: ['#0F5A3E', '#13744A', '#25915B'],
    trunk: '#4A3530',
    farKerb: '#8FA9CC',
    road: '#314E70',
    dash: '#C5D0E2',
    kerb: '#A6BEE1',
    pavement: '#A9AAB8',
    joint: '#9395A6',
    bushes: ['#0E5A3C', '#17784D', '#2A9460'],
    body: '#E7ECF3',
    bodyShade: '#BCC7D6',
    glass: '#1F3658',
    tyre: '#1E2433',
    rim: '#9AA6B8',
    logo: '#0A93D6',
    pin: '#0A93D6',
    skin: '#E09574',
    hair: '#2A1C26',
    coat: '#C95F4E',
    coatShade: '#A84D40',
    trousers: '#1F4478',
    bag: '#D49A36',
    bagStrap: '#9A6E26',
    shoe: '#1A1F2E',
    cat: '#D98F50',
    catLight: '#E8DCCF',
    pole: '#34425C',
    wood: '#8E5D42',
    shadow: 'rgb(10 20 40 / 26%)',
  },
  night: {
    skyTop: '#001A43',
    skyLow: '#13306A',
    cloud: '#1D3874',
    farTowers: '#1B3770',
    towers: '#0E224B',
    window: '#1C3366',
    lit: '#FFCB72',
    trees: ['#06261D', '#0B3A2B', '#11503A'],
    trunk: '#1F1A22',
    farKerb: '#3B4766',
    road: '#0F2240',
    dash: '#5F6C8C',
    kerb: '#4C5878',
    pavement: '#2D3756',
    joint: '#252E4A',
    bushes: ['#062A1E', '#0C3D2B', '#14533B'],
    body: '#C9D2E0',
    bodyShade: '#97A3B8',
    glass: '#101D38',
    tyre: '#10141F',
    rim: '#6E7A90',
    logo: '#0B74B0',
    pin: '#2BB3F5',
    skin: '#C87D62',
    hair: '#1A1220',
    coat: '#A44E44',
    coatShade: '#843E37',
    trousers: '#17315E',
    bag: '#8E6A2E',
    bagStrap: '#5E4520',
    shoe: '#0E121D',
    cat: '#B87644',
    catLight: '#CFC3B8',
    pole: '#222C44',
    wood: '#4E3529',
    shadow: 'rgb(0 0 10 / 35%)',
  },
};

/** The bodies on the road. Ours are all saloons; everybody else drives what they like. */
type Model = 'saloon' | 'hatch' | 'suv' | 'van';

/** A car going past: which lane, how long a lap of the road takes, how far into it. */
interface Pass {
  near: boolean;
  lap: number;
  phase: number;
  /**
   * Somebody else's car, in this colour and this body; ours are white saloons with
   * the mark on the door.
   */
  other?: { tint: string; model: Model };
  /**
   * Seconds into the last minute after which the car comes round no more. It
   * finishes the lap it is on, so it never drops out of the road.
   */
  until?: number;
}

const RED = '#C9524A';
const MUSTARD = '#E2A93E';
const NAVY = '#3E5C8A';
const GREY = '#7A8798';
const GREEN = '#4E8F6A';
const BLACK = '#2D3340';
const SILVER = '#B7BFCB';

/**
 * The traffic, stage by stage. Near-lane cars go right, from where the passenger
 * looks up the road; the far lane goes left. A lap is the road plus room either side
 * for the car to be gone, so a long lap is a quiet road rather than a slow car: the
 * street is busiest once everybody can see the order, and goes quiet as the search
 * runs long, and in the last minute it empties — the cars stop coming round 7, 13,
 * 20 and 30 seconds in, and the last of them is gone by 40. Every car in a lane
 * keeps the lane's lap, so none drives through another.
 *
 * Most of the town's cars are not ours — one or two in a stage are, which is what a
 * street looks like, and is what makes the one the pin drops on stand out.
 */
const traffic: Record<SearchStage, Pass[]> = {
  offering: [
    { near: true, lap: 9, phase: 0.15 },
    { near: true, lap: 9, phase: 0.62, other: { tint: RED, model: 'hatch' } },
    { near: false, lap: 12, phase: 0.55, other: { tint: SILVER, model: 'suv' } },
    { near: false, lap: 12, phase: 0.05, other: { tint: MUSTARD, model: 'van' } },
  ],
  open: [
    { near: true, lap: 6, phase: 0.1 },
    { near: true, lap: 6, phase: 0.45, other: { tint: NAVY, model: 'hatch' } },
    { near: true, lap: 6, phase: 0.78, other: { tint: GREEN, model: 'suv' } },
    { near: false, lap: 7, phase: 0.3 },
    { near: false, lap: 7, phase: 0.62, other: { tint: GREY, model: 'saloon' } },
    { near: false, lap: 7, phase: 0.9, other: { tint: BLACK, model: 'suv' } },
  ],
  continuing: [
    { near: true, lap: 7, phase: 0.3 },
    { near: true, lap: 7, phase: 0.75, other: { tint: RED, model: 'saloon' } },
    { near: false, lap: 9, phase: 0.5, other: { tint: SILVER, model: 'hatch' } },
    { near: false, lap: 9, phase: 0.05, other: { tint: MUSTARD, model: 'van' } },
  ],
  longer: [
    { near: true, lap: 12, phase: 0.2, other: { tint: GREY, model: 'suv' } },
    { near: false, lap: 14, phase: 0.6 },
    { near: false, lap: 14, phase: 0.1, other: { tint: GREEN, model: 'hatch' } },
  ],
  lastMinute: [
    { near: true, lap: 10, phase: 0.45, until: 30 },
    { near: true, lap: 10, phase: 0.9, other: { tint: BLACK, model: 'hatch' }, until: 13 },
    { near: false, lap: 12, phase: 0.1, other: { tint: NAVY, model: 'van' }, until: 7 },
    { near: false, lap: 12, phase: 0.55, other: { tint: RED, model: 'suv' }, until: 20 },
  ],
};

/** How long a car is, nose to tail, in the near lane; the far lane is drawn smaller. */
export const CAR = 250;
const FAR_SCALE = 0.78;

/** The road a lap covers: the whole picture and a car's length off each side. */
const RUN = W + 2 * CAR;

/**
 * Whether a car is on the lap it is on at `t`, with `left` seconds of the search to
 * go then: only if that lap began before its `until`.
 */
function comesRound(pass: Pass, t: number, left: number | null): boolean {
  if (pass.until === undefined || left === null) return true;
  const begun = left + ((t / pass.lap + pass.phase) % 1) * pass.lap;
  return begun > MINUTE - pass.until;
}

/**
 * Where each of `stage`'s cars is on the road at `t`, with `left` seconds of the
 * search to go: its left edge, or null once it has stopped coming round.
 */
export function carsAt(stage: SearchStage, t: number, left: number | null): (number | null)[] {
  return traffic[stage].map((pass) => (comesRound(pass, t, left) ? carX(pass, t) : null));
}

/** The left edge of a car at `t`. */
function carX(pass: Pass, t: number): number {
  const into = (t / pass.lap + pass.phase) % 1;
  return pass.near ? -CAR + into * RUN : W + CAR * (1 - FAR_SCALE) - into * RUN;
}

/** Where the passenger is and what they are doing, at one instant. */
interface Pose {
  x: number;
  /** 1 facing right, -1 facing left — the figure is drawn mirrored. */
  dir: 1 | -1;
  kind: 'stand' | 'walk' | 'sit';
  /** 0 looking at the phone, 1 turned to look up the road. */
  look: number;
  /** How far the legs are apart in a stride, in radians either way. */
  swing: number;
  /** How far the body has risen or sunk, with a breath or a step. */
  bob: number;
}

/** How far a seated passenger's shoulders sink below a standing one's. */
const SIT_DROP = 36;

function pose(stage: SearchStage, t: number): Pose {
  const breath = Math.sin(t * 1.6) * 1.2;
  switch (stage) {
    case 'continuing': {
      // Up and down the kerb, nine seconds there and back, slowing at each end.
      const along = (2 * Math.PI * t) / 9;
      const speed = Math.min(1, Math.abs(Math.cos(along)) * 1.6);
      return {
        x: PASSENGER + 70 * Math.sin(along),
        dir: Math.cos(along) >= 0 ? 1 : -1,
        kind: 'walk',
        look: 0,
        swing: Math.sin(t * 7) * 0.42 * speed,
        bob: -Math.abs(Math.sin(t * 7)) * 2.4 * speed,
      };
    }
    case 'longer':
      return { x: SEAT, dir: 1, kind: 'sit', look: glance(t, 10, 7.6), swing: 0, bob: breath };
    case 'lastMinute':
      // Up the road, and down at the phone now and then.
      return { x: PASSENGER, dir: 1, kind: 'stand', look: 1 - glance(t, 9, 5), swing: 0, bob: breath };
    default:
      return { x: PASSENGER, dir: 1, kind: 'stand', look: glance(t, 8, 5.4), swing: 0, bob: breath };
  }
}

/**
 * Up from the phone for a moment once every `cycle` seconds, from `from` into it —
 * 0 while reading, 1 while looking up the road, with a short turn either side.
 */
function glance(t: number, cycle: number, from: number): number {
  const c = t % cycle;
  const up = from + 0.4;
  const down = from + 2.0;
  if (c < from) return 0;
  if (c < up) return (c - from) / 0.4;
  if (c < down) return 1;
  if (c < down + 0.4) return (down + 0.4 - c) / 0.4;
  return 0;
}

/** Where the phone is, in the plan, for a passenger in `p`. */
function phoneAt(p: Pose): [number, number] {
  return [p.x + p.dir * 38, FEET - 134 + (p.kind === 'sit' ? SIT_DROP : 0) + p.bob];
}

/**
 * Paints the street at `t` seconds into a `width` × `height` box, cropping the plan
 * from the middle so that it covers the box.
 */
export function paintStreet(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  t: number,
  options: SceneOptions,
): void {
  const p = palettes[options.sky];
  const night = options.sky === 'night';
  const stage = options.stage;
  const scale = Math.max(width / W, height / H);
  ctx.save();
  ctx.translate((width - W * scale) / 2, (height - H * scale) / 2);
  ctx.scale(scale, scale);

  sky(ctx, p, night, t);
  clouds(ctx, p, t);
  town(ctx, p, night, t);
  trees(ctx, p, t);
  road(ctx, p);

  const left = options.end == null ? null : Math.max(0, options.end - t);
  const cars = traffic[stage].filter((pass) => comesRound(pass, t, left));
  const who = pose(stage, t);
  // The far lane first, so the near one passes in front of it.
  for (const pass of cars.filter((c) => !c.near)) car(ctx, p, options.sky, pass, t);
  for (const pass of cars.filter((c) => c.near)) car(ctx, p, options.sky, pass, t);
  // The pin is on the road with its car, so the lamp and the passenger stand in
  // front of it as it goes past them.
  if (stage === 'offering') {
    for (const pass of cars) offer(ctx, p, pass, who.x, t);
  }

  pavement(ctx, p);
  lamp(ctx, p, night);
  bench(ctx, p);
  if (stage === 'lastMinute') watchingCat(ctx, p, t);
  else cat(ctx, p, t, stage === 'longer');
  passenger(ctx, p, night, who);
  if (stage === 'lastMinute') overhead(ctx, who, t, left);
  else if (stage !== 'offering') rings(ctx, stage, phoneAt(who), t);
  bushes(ctx, p, t);

  ctx.restore();
}

function sky(ctx: CanvasRenderingContext2D, p: Palette, night: boolean, t: number): void {
  const g = ctx.createLinearGradient(0, 0, 0, HORIZON);
  g.addColorStop(0, p.skyTop);
  g.addColorStop(1, p.skyLow);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
  if (!night) return;
  // A few stars, each on its own slow breath.
  for (let i = 0; i < 26; i++) {
    const x = (i * 337 + 41) % W;
    const y = 12 + ((i * 151) % 190);
    const breath = 0.45 + 0.4 * Math.sin(t * (0.6 + (i % 5) * 0.17) + i * 1.7);
    ctx.fillStyle = `rgb(255 255 255 / ${Math.round(breath * 100)}%)`;
    ctx.beginPath();
    ctx.arc(x, y, i % 4 === 0 ? 2.2 : 1.4, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** Three clouds drifting right, each at its own height and pace, wrapping round. */
function clouds(ctx: CanvasRenderingContext2D, p: Palette, t: number): void {
  const drift = [
    { y: 92, size: 1.15, speed: 7, from: 120 },
    { y: 60, size: 0.9, speed: 5, from: 640 },
    { y: 150, size: 0.6, speed: 9, from: 420 },
  ];
  ctx.fillStyle = p.cloud;
  for (const c of drift) {
    const span = W + 360;
    const x = ((c.from + t * c.speed) % span) - 180;
    cloud(ctx, x, c.y, c.size);
  }
}

function cloud(ctx: CanvasRenderingContext2D, x: number, y: number, s: number): void {
  ctx.beginPath();
  circle(ctx, x - 38 * s, y + 4 * s, 26 * s);
  circle(ctx, x, y - 12 * s, 38 * s);
  circle(ctx, x + 44 * s, y + 2 * s, 28 * s);
  ctx.fill();
  roundRect(ctx, x - 92 * s, y + 8 * s, 184 * s, 22 * s, 11 * s);
  ctx.fill();
}

/** The town: a pale far row and a nearer, deeper one, with their windows. */
function town(ctx: CanvasRenderingContext2D, p: Palette, night: boolean, t: number): void {
  const far: [number, number, number][] = [
    [10, 70, 150],
    [95, 55, 120],
    [170, 80, 175],
    [300, 60, 135],
    [380, 90, 190],
    [560, 70, 145],
    [650, 60, 205],
    [740, 85, 160],
    [880, 70, 185],
    [965, 80, 140],
  ];
  ctx.fillStyle = p.farTowers;
  for (const [x, w, h] of far) ctx.fillRect(x, HORIZON - h, w, h);

  const near: [number, number, number][] = [
    [40, 64, 210],
    [230, 58, 175],
    [455, 72, 250],
    [600, 48, 160],
    [790, 66, 225],
    [930, 56, 190],
  ];
  for (const [x, w, h] of near) {
    const top = HORIZON - h;
    ctx.fillStyle = p.towers;
    ctx.fillRect(x, top, w, h);
    // A mast on the tallest.
    if (h >= 240) ctx.fillRect(x + w / 2 - 2, top - 30, 4, 30);
    for (let row = 0; top + 16 + row * 22 < HORIZON - 30; row++) {
      for (let col = 0; col * 16 + 10 < w - 6; col++) {
        // At night some windows are on, a few go on and off over the minutes.
        const seed = (x * 7 + row * 13 + col * 29) % 17;
        let on = !night || seed < 7;
        if (night && seed === 3) on = Math.sin(t * 0.25 + x) > 0;
        if (!night && seed % 3 !== 0) continue;
        ctx.fillStyle = on ? p.lit : p.window;
        ctx.fillRect(x + 8 + col * 16, top + 14 + row * 22, 8, 11);
      }
    }
  }
}

/** The trees between the town and the road, swaying a little. */
function trees(ctx: CanvasRenderingContext2D, p: Palette, t: number): void {
  const row: [number, number, 0 | 1 | 2][] = [
    [-10, 62, 0],
    [70, 48, 1],
    [150, 56, 2],
    [250, 44, 0],
    [330, 60, 1],
    [430, 50, 2],
    [520, 58, 0],
    [610, 46, 1],
    [700, 62, 2],
    [800, 50, 0],
    [880, 58, 1],
    [980, 64, 2],
    [1060, 50, 0],
  ];
  for (const [x, r, shade] of row) {
    const sway = Math.sin(t * 0.8 + x * 0.05) * 1.6;
    ctx.fillStyle = p.trunk;
    ctx.fillRect(x - 3, HORIZON - r * 0.9, 6, r * 0.9);
    ctx.fillStyle = p.trees[shade];
    ctx.beginPath();
    circle(ctx, x + sway, HORIZON - r * 1.05, r * 0.7);
    circle(ctx, x - r * 0.42 + sway * 0.6, HORIZON - r * 0.62, r * 0.55);
    circle(ctx, x + r * 0.45 + sway * 0.6, HORIZON - r * 0.6, r * 0.52);
    ctx.fill();
  }
  // A low hedge closes the gaps along the far kerb.
  ctx.fillStyle = p.trees[1];
  for (let x = -20; x < W + 40; x += 34) {
    ctx.beginPath();
    ctx.arc(x, HORIZON - 4, 22, Math.PI, 0);
    ctx.fill();
  }
}

function road(ctx: CanvasRenderingContext2D, p: Palette): void {
  ctx.fillStyle = p.farKerb;
  ctx.fillRect(0, HORIZON - 4, W, 12);
  ctx.fillStyle = p.road;
  ctx.fillRect(0, HORIZON + 8, W, KERB - HORIZON - 8);
  ctx.fillStyle = p.dash;
  const middle = (FAR_LANE + NEAR_LANE) / 2 - 12;
  for (let x = 10; x < W; x += 110) {
    roundRect(ctx, x, middle, 56, 5, 2.5);
    ctx.fill();
  }
}

function pavement(ctx: CanvasRenderingContext2D, p: Palette): void {
  ctx.fillStyle = p.kerb;
  ctx.fillRect(0, KERB, W, 9);
  ctx.fillStyle = p.pavement;
  ctx.fillRect(0, KERB + 9, W, H - KERB - 9);
  ctx.strokeStyle = p.joint;
  ctx.lineWidth = 2;
  ctx.beginPath();
  for (let x = 40; x < W; x += 120) {
    ctx.moveTo(x, KERB + 9);
    ctx.lineTo(x - 30, H);
  }
  ctx.moveTo(0, KERB + 44);
  ctx.lineTo(W, KERB + 44);
  ctx.stroke();
}

/**
 * The lamp on the pavement behind the passenger, its head out over the road. At night
 * it is lit, with a pool of light round its foot.
 */
function lamp(ctx: CanvasRenderingContext2D, p: Palette, night: boolean): void {
  const x = 860;
  const foot = FEET - 22;
  if (night) {
    const pool = ctx.createRadialGradient(x - 40, foot + 10, 4, x - 40, foot + 10, 180);
    pool.addColorStop(0, 'rgb(255 214 140 / 36%)');
    pool.addColorStop(1, 'rgb(255 214 140 / 0%)');
    ctx.fillStyle = pool;
    ctx.fillRect(x - 230, KERB + 9, 400, H - KERB - 9);
  }
  ctx.fillStyle = p.pole;
  ctx.fillRect(x - 4, 250, 8, foot - 250);
  roundRect(ctx, x - 9, foot - 14, 18, 16, 4);
  ctx.fill();
  roundRect(ctx, x - 56, 244, 56, 8, 4);
  ctx.fill();
  roundRect(ctx, x - 66, 246, 26, 12, 5);
  ctx.fill();
  if (!night) return;
  const glow = ctx.createRadialGradient(x - 53, 262, 2, x - 53, 262, 90);
  glow.addColorStop(0, 'rgb(255 222 160 / 85%)');
  glow.addColorStop(0.15, 'rgb(255 214 140 / 40%)');
  glow.addColorStop(1, 'rgb(255 214 140 / 0%)');
  ctx.fillStyle = glow;
  ctx.fillRect(x - 143, 172, 180, 180);
}

/** The bench along the pavement: empty until the search has run long. */
function bench(ctx: CanvasRenderingContext2D, p: Palette): void {
  const seat = FEET - 46;
  ctx.fillStyle = p.pole;
  ctx.fillRect(BENCH + 8, seat, 6, 46);
  ctx.fillRect(BENCH + 96, seat, 6, 46);
  ctx.fillRect(BENCH + 8, seat - 44, 5, 44);
  ctx.fillRect(BENCH + 97, seat - 44, 5, 44);
  ctx.fillStyle = p.wood;
  for (const y of [seat - 42, seat - 28]) {
    roundRect(ctx, BENCH, y, 110, 9, 3);
    ctx.fill();
  }
  roundRect(ctx, BENCH - 4, seat - 6, 118, 9, 3);
  ctx.fill();
}

/** A path as a list of steps — the same lists `search_street.dart` traces. */
type Step = ['M' | 'L', number, number] | ['Q', number, number, number, number] | ['Z'];

/**
 * A body seen from the side, facing right with its wheels on y = 0: its outline, its
 * glass, the pillars that cut the glass, the wheels, and the lamps at either end.
 */
interface Body {
  length: number;
  outline: Step[];
  glass: Step[];
  pillars: number[];
  /** How high the glass runs, for the pillars across it. */
  roof: number;
  sill: number;
  wheels: number[];
  /** The wheels' radius; their centres are this high minus 7. */
  wheel: number;
  tail: [number, number, number, number];
  head: [number, number, number, number];
}

const bodies: Record<Model, Body> = {
  saloon: {
    length: 250,
    outline: [
      ['M', 10, -12], ['Q', 2, -14, 3, -30], ['Q', 4, -44, 18, -48], ['L', 60, -52],
      ['Q', 80, -74, 104, -76], ['L', 146, -76], ['Q', 166, -74, 190, -53],
      ['Q', 232, -48, 242, -40], ['Q', 250, -30, 246, -14], ['Q', 244, -10, 236, -10], ['Z'],
    ],
    glass: [['M', 70, -52], ['Q', 86, -68, 104, -70], ['L', 144, -70], ['Q', 160, -68, 180, -52], ['Z']],
    pillars: [122],
    roof: -72,
    sill: -50,
    wheels: [56, 196],
    wheel: 21,
    tail: [3, -42, 10, 7],
    head: [236, -40, 10, 6],
  },
  hatch: {
    length: 215,
    outline: [
      ['M', 10, -12], ['Q', 2, -14, 3, -32], ['Q', 4, -52, 14, -60], ['L', 40, -78],
      ['Q', 60, -81, 100, -80], ['L', 118, -79], ['Q', 136, -77, 160, -54],
      ['Q', 200, -48, 208, -40], ['Q', 215, -30, 211, -14], ['Q', 209, -10, 201, -10], ['Z'],
    ],
    glass: [['M', 22, -58], ['L', 44, -73], ['L', 114, -73], ['Q', 128, -71, 150, -54], ['L', 22, -54], ['Z']],
    pillars: [36, 92],
    roof: -75,
    sill: -52,
    wheels: [46, 168],
    wheel: 21,
    tail: [4, -46, 8, 10],
    head: [202, -40, 9, 6],
  },
  suv: {
    length: 255,
    outline: [
      ['M', 10, -14], ['Q', 3, -16, 4, -34], ['L', 8, -84], ['Q', 10, -92, 22, -92],
      ['L', 150, -92], ['Q', 164, -92, 176, -80], ['L', 196, -58], ['Q', 240, -54, 248, -44],
      ['Q', 254, -32, 250, -16], ['Q', 248, -12, 240, -12], ['Z'],
    ],
    glass: [['M', 18, -60], ['L', 20, -84], ['L', 148, -84], ['Q', 158, -84, 166, -76], ['L', 184, -60], ['Z']],
    pillars: [72, 126],
    roof: -86,
    sill: -58,
    wheels: [58, 198],
    wheel: 24,
    tail: [5, -52, 8, 12],
    head: [240, -46, 10, 7],
  },
  van: {
    length: 270,
    outline: [
      ['M', 10, -14], ['Q', 3, -16, 4, -34], ['L', 6, -100], ['Q', 8, -108, 20, -108],
      ['L', 176, -108], ['Q', 196, -106, 214, -80], ['L', 232, -58], ['Q', 262, -54, 266, -44],
      ['Q', 270, -30, 266, -16], ['Q', 264, -12, 256, -12], ['Z'],
    ],
    glass: [['M', 22, -98], ['L', 172, -98], ['Q', 190, -96, 206, -74], ['L', 212, -66], ['L', 22, -66], ['Z']],
    pillars: [70, 120, 170],
    roof: -100,
    sill: -64,
    wheels: [54, 216],
    wheel: 22,
    tail: [5, -58, 8, 14],
    head: [256, -46, 10, 7],
  },
};

function trace(ctx: CanvasRenderingContext2D, steps: Step[]): void {
  ctx.beginPath();
  for (const step of steps) {
    if (step[0] === 'M') ctx.moveTo(step[1], step[2]);
    else if (step[0] === 'L') ctx.lineTo(step[1], step[2]);
    else if (step[0] === 'Q') ctx.quadraticCurveTo(step[1], step[2], step[3], step[4]);
    else ctx.closePath();
  }
}

/**
 * Somebody else's paint under this sky: as it is by day, a little into the blue on
 * the darker daylight street, and well into it at night, as the town is.
 */
function under(tint: string, sky: Sky): string {
  const k = sky === 'night' ? 0.45 : sky === 'dark' ? 0.15 : 0;
  const mix = (at: number, to: number) => {
    const c = parseInt(tint.slice(at, at + 2), 16);
    return Math.round(c + (to - c) * k);
  };
  return `rgb(${mix(1, 10)} ${mix(3, 20)} ${mix(5, 48)})`;
}

/** One car going past, drawn facing right and flipped for the far lane. */
function car(ctx: CanvasRenderingContext2D, p: Palette, sky: Sky, pass: Pass, t: number): void {
  const night = sky === 'night';
  const s = pass.near ? 1 : FAR_SCALE;
  const ground = pass.near ? NEAR_LANE : FAR_LANE;
  const shape = bodies[pass.other?.model ?? 'saloon'];
  const length = shape.length;
  const x = carX(pass, t);
  const paint = pass.other ? under(pass.other.tint, sky) : p.body;

  ctx.save();
  if (pass.near) ctx.translate(x, ground);
  else {
    ctx.translate(x + length * s, ground);
    ctx.scale(-1, 1);
  }
  ctx.scale(s, s);

  // Its shadow on the road.
  ctx.fillStyle = p.shadow;
  ctx.beginPath();
  ctx.ellipse(length / 2, 2, length * 0.5, 7, 0, 0, Math.PI * 2);
  ctx.fill();

  // At night the headlights throw a cone ahead.
  if (night) {
    const beam = ctx.createLinearGradient(length - 6, 0, length + 190, 0);
    beam.addColorStop(0, 'rgb(255 226 160 / 50%)');
    beam.addColorStop(1, 'rgb(255 226 160 / 0%)');
    ctx.fillStyle = beam;
    ctx.beginPath();
    ctx.moveTo(length - 8, -40);
    ctx.lineTo(length + 190, -62);
    ctx.lineTo(length + 190, 4);
    ctx.lineTo(length - 8, -28);
    ctx.fill();
  }

  // The body, and the sill along its foot a shade darker, so it has a side to it.
  ctx.fillStyle = paint;
  trace(ctx, shape.outline);
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.fillStyle = pass.other ? 'rgb(0 0 0 / 18%)' : p.bodyShade;
  ctx.fillRect(0, -22, length, 22);
  ctx.restore();

  // The glass, cut by its pillars, with a glint across it.
  ctx.fillStyle = p.glass;
  trace(ctx, shape.glass);
  ctx.fill();
  ctx.fillStyle = paint;
  for (const pillar of shape.pillars) ctx.fillRect(pillar, shape.roof, 6, shape.sill - shape.roof + 2);
  const glint = shape.pillars[0]! + 14;
  ctx.fillStyle = 'rgb(255 255 255 / 18%)';
  ctx.beginPath();
  ctx.moveTo(glint, shape.roof + 2);
  ctx.lineTo(glint + 10, shape.roof + 2);
  ctx.lineTo(glint - 4, shape.sill);
  ctx.lineTo(glint - 14, shape.sill);
  ctx.fill();

  // The mark on the door, the right way round whichever way the car is going.
  if (!pass.other) mark(ctx, p.logo, 128, -32, 58, !pass.near);

  // Lights: tail red, head pale — brighter at night.
  ctx.fillStyle = night ? '#FF4A4A' : '#E5484D';
  roundRect(ctx, ...shape.tail, 3);
  ctx.fill();
  ctx.fillStyle = night ? '#FFF2C4' : '#EAF2FF';
  roundRect(ctx, ...shape.head, 3);
  ctx.fill();

  // The wheels, turning.
  const spin = (t * 9) % (Math.PI * 2);
  for (const wx of shape.wheels) wheel(ctx, p, wx, 7 - shape.wheel, shape.wheel, spin);

  ctx.restore();
}

function wheel(ctx: CanvasRenderingContext2D, p: Palette, x: number, y: number, r: number, spin: number): void {
  ctx.fillStyle = 'rgb(0 0 0 / 22%)';
  ctx.beginPath();
  ctx.arc(x, y, r + 5, Math.PI, 0);
  ctx.fill();
  ctx.fillStyle = p.tyre;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = p.rim;
  ctx.beginPath();
  ctx.arc(x, y, r * 0.57, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = p.tyre;
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  for (let i = 0; i < 5; i++) {
    const a = spin + (i * Math.PI * 2) / 5;
    ctx.moveTo(x, y);
    ctx.lineTo(x + Math.cos(a) * r * 0.52, y + Math.sin(a) * r * 0.52);
  }
  ctx.stroke();
}

let markPath: Path2D | null = null;

/**
 * The mark, `width` wide and centred on `x`, `y`. `mirrored` undoes the flip a car in
 * the far lane is drawn with, so the mark never reads backwards.
 */
function mark(
  ctx: CanvasRenderingContext2D,
  colour: string,
  x: number,
  y: number,
  width: number,
  mirrored: boolean,
): void {
  markPath ??= new Path2D(MARK);
  const s = width / 100;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(mirrored ? -s : s, s);
  ctx.translate(-50, -55.59 / 2);
  ctx.fillStyle = colour;
  ctx.fill(markPath, 'evenodd');
  ctx.restore();
}

/**
 * While the order is offered, it goes to one of our cars at a time: as the car comes
 * up the road towards the passenger, a pin with the mark drops onto its roof,
 * bounces, and rides along on it until the car is past, when it lifts off and is
 * gone. Each of ours gets it in turn as it comes past, and nobody else's.
 */
function offer(ctx: CanvasRenderingContext2D, p: Palette, pass: Pass, passenger: number, t: number): void {
  if (pass.other) return;
  const s = pass.near ? 1 : FAR_SCALE;
  const ground = pass.near ? NEAR_LANE : FAR_LANE;
  const centre = carX(pass, t) + (CAR * s) / 2;
  // How far the car has come towards the passenger, either lane: rising as it nears.
  const coming = pass.near ? centre - passenger : passenger - centre;
  const progress = (coming + 380) / 300;
  if (progress < 0.2 || progress > 2.55) return;

  const fall = Math.min(1, (progress - 0.2) / 0.5);
  const leave = Math.max(0, (progress - 2.2) / 0.35);
  const roof = ground - 77 * s;
  const lift = (1 - bounce(fall)) * 300 + leave * 60;

  // Its shadow on the roof, sharper as it comes down.
  ctx.fillStyle = `rgb(10 20 50 / ${Math.round(30 * fall * (1 - leave))}%)`;
  ctx.beginPath();
  ctx.ellipse(centre, roof + 2, 12 * (0.5 + 0.5 * fall) * s, 3 * s, 0, 0, Math.PI * 2);
  ctx.fill();

  // A ripple across the roof where it lands.
  const ripple = (progress - 0.62) / 0.5;
  if (ripple > 0 && ripple < 1) {
    ctx.save();
    ctx.globalAlpha = 1 - ripple;
    ctx.strokeStyle = p.pin;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.ellipse(centre, roof + 2, (18 + ripple * 70) * s, (4 + ripple * 14) * s, 0, 0, Math.PI * 2);
    ctx.stroke();
    ctx.restore();
  }

  // The pin, tip down on the roof, swaying a little once it is there.
  ctx.save();
  ctx.globalAlpha = 1 - leave;
  ctx.translate(centre, roof - lift);
  ctx.scale(s, s);
  if (fall >= 1) ctx.rotate(Math.sin(t * 3) * 0.05);
  ctx.fillStyle = p.pin;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(-6, -14, -20, -22, -20, -38);
  ctx.arc(0, -38, 20, Math.PI, 0);
  ctx.bezierCurveTo(20, -22, 6, -14, 0, 0);
  ctx.fill();
  ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  ctx.arc(0, -38, 13.5, 0, Math.PI * 2);
  ctx.fill();
  mark(ctx, p.pin, 0, -38, 21, false);
  ctx.restore();
}

/** A fall that lands, bounces twice, and settles: 0 in the air, 1 down. */
function bounce(x: number): number {
  const n = 7.5625;
  const d = 2.75;
  if (x < 1 / d) return n * x * x;
  if (x < 2 / d) return n * (x - 1.5 / d) ** 2 + 0.75;
  if (x < 2.5 / d) return n * (x - 2.25 / d) ** 2 + 0.9375;
  return n * (x - 2.625 / d) ** 2 + 0.984375;
}

/**
 * The order going out from the phone, once it is not one car's but everybody's:
 * three rings at once and out past the road while it is new, one at a time while the
 * passenger walks, and small and slow once they have sat down. In the last minute the
 * pin over the passenger says it instead.
 */
function rings(ctx: CanvasRenderingContext2D, stage: SearchStage, phone: [number, number], t: number): void {
  const shape = {
    open: { phases: [0, 1 / 3, 2 / 3], period: 3, reach: 260 },
    continuing: { phases: [0], period: 2.5, reach: 150 },
    longer: { phases: [0], period: 4, reach: 90 },
    lastMinute: { phases: [], period: 1, reach: 0 },
    offering: { phases: [], period: 1, reach: 0 },
  }[stage];
  ctx.lineWidth = 3.5;
  for (const phase of shape.phases) {
    const into = (t / shape.period + phase) % 1;
    const r = 14 + into * shape.reach;
    ctx.strokeStyle = `rgb(255 255 255 / ${Math.round((1 - into) * 85)}%)`;
    ctx.beginPath();
    ctx.arc(phone[0], phone[1], r, Math.PI * 1.1, Math.PI * 1.9);
    ctx.stroke();
  }
}

/**
 * The passenger. Standing at the kerb with the phone and looking up the road now and
 * then; walking up and down it; or sat on the bench. The head turns by narrowing and opening out the other way round, which at
 * this size reads as a turn.
 */
function passenger(ctx: CanvasRenderingContext2D, p: Palette, night: boolean, who: Pose): void {
  const sitting = who.kind === 'sit';
  ctx.save();
  ctx.translate(who.x, FEET);
  ctx.scale(who.dir, 1);

  ctx.fillStyle = p.shadow;
  ctx.beginPath();
  ctx.ellipse(sitting ? 18 : 4, 2, 44, 7, 0, 0, Math.PI * 2);
  ctx.fill();

  if (!sitting) {
    // The legs, each swinging from its hip, the far one first.
    for (const [hip, angle] of [
      [9.5, -who.swing],
      [-8.5, who.swing],
    ] as const) {
      ctx.save();
      ctx.translate(hip, -84 + who.bob);
      ctx.rotate(angle);
      ctx.fillStyle = p.trousers;
      roundRect(ctx, -7.5, 0, 15, 82 - who.bob, 7);
      ctx.fill();
      ctx.fillStyle = p.shoe;
      roundRect(ctx, -10, 76 - who.bob, 22, 9, 4);
      ctx.fill();
      ctx.restore();
    }
  }

  ctx.translate(0, who.bob + (sitting ? SIT_DROP : 0));

  // The far arm, hanging.
  ctx.fillStyle = p.coatShade;
  roundRect(ctx, -31, -146, 14, 66, 7);
  ctx.fill();

  // The coat: narrower at the waist, out a little at the hem, to the knee.
  ctx.fillStyle = p.coat;
  ctx.beginPath();
  ctx.moveTo(-22, -152);
  ctx.quadraticCurveTo(0, -158, 22, -152);
  ctx.quadraticCurveTo(28, -148, 26, -134);
  ctx.lineTo(22, -108);
  ctx.lineTo(30, -66);
  ctx.quadraticCurveTo(0, -60, -30, -66);
  ctx.lineTo(-22, -108);
  ctx.lineTo(-26, -134);
  ctx.quadraticCurveTo(-28, -148, -22, -152);
  ctx.fill();
  // The belt, the opening, and the collar.
  ctx.fillStyle = p.coatShade;
  ctx.fillRect(-22, -110, 44, 5);
  ctx.fillRect(-1, -146, 3, 82);
  ctx.beginPath();
  ctx.moveTo(-11, -154);
  ctx.lineTo(1, -128);
  ctx.lineTo(13, -154);
  ctx.fill();
  ctx.fillStyle = p.skin;
  ctx.beginPath();
  ctx.moveTo(-6, -154);
  ctx.lineTo(1, -138);
  ctx.lineTo(8, -154);
  ctx.fill();

  if (sitting) {
    // Sat down: the thighs along the seat and the shins down from the knee.
    ctx.fillStyle = p.trousers;
    for (const dx of [-4, 4]) {
      roundRect(ctx, -12 + dx, -94, 52, 15, 7);
      ctx.fill();
      roundRect(ctx, 26 + dx, -92, 15, 92 - SIT_DROP, 7);
      ctx.fill();
    }
    ctx.fillStyle = p.shoe;
    roundRect(ctx, 24, -SIT_DROP - 9, 24, 9, 4);
    ctx.fill();
  }

  // The strap across the coat and the bag at the far hip.
  ctx.strokeStyle = p.bagStrap;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(16, -150);
  ctx.lineTo(-26, -96);
  ctx.stroke();
  ctx.fillStyle = p.bag;
  roundRect(ctx, -40, -104, 26, 22, 5);
  ctx.fill();
  ctx.fillStyle = p.bagStrap;
  ctx.fillRect(-40, -97, 26, 3);

  // The neck, and the head on it: tipped down to the phone, or up and round to the
  // road.
  ctx.fillStyle = p.skin;
  ctx.fillRect(-5, -164, 10, 12);
  ctx.save();
  ctx.translate(0, -162);
  ctx.rotate(0.12 - who.look * 0.16);
  // Never quite edge-on: a head that thins to nothing for a frame reads as a blink
  // of the whole person.
  const turn = Math.cos(who.look * Math.PI);
  ctx.scale((turn < 0 ? -1 : 1) * Math.max(Math.abs(turn), 0.3), 1);
  // The bun behind, the face, the hair over it, and one eye.
  ctx.fillStyle = p.hair;
  ctx.beginPath();
  ctx.arc(-9, -30, 9, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = p.skin;
  ctx.beginPath();
  ctx.arc(0, -16, 16, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = p.hair;
  ctx.beginPath();
  ctx.moveTo(-17, -10);
  ctx.quadraticCurveTo(-20, -34, 0, -34);
  ctx.quadraticCurveTo(16, -34, 17, -20);
  ctx.quadraticCurveTo(4, -26, -6, -18);
  ctx.quadraticCurveTo(-8, -10, -17, -10);
  ctx.fill();
  ctx.fillStyle = p.shoe;
  ctx.beginPath();
  ctx.arc(9, -14, 1.9, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // The near arm, bent, with the phone held up in front.
  ctx.strokeStyle = p.coat;
  ctx.lineWidth = 14;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(17, -142);
  ctx.lineTo(23, -108);
  ctx.lineTo(34, -122);
  ctx.stroke();
  ctx.fillStyle = p.skin;
  ctx.beginPath();
  ctx.arc(36, -126, 6, 0, Math.PI * 2);
  ctx.fill();
  ctx.save();
  ctx.translate(38, -134);
  ctx.rotate(-0.25);
  ctx.fillStyle = '#1A1F2E';
  roundRect(ctx, -5, -9, 10, 18, 2.5);
  ctx.fill();
  ctx.fillStyle = night ? '#BFE3FF' : '#8FCBFF';
  roundRect(ctx, -3.5, -7.5, 7, 15, 1.5);
  ctx.fill();
  ctx.restore();
  if (night) {
    // The screen lights the face from under it.
    const glow = ctx.createRadialGradient(36, -140, 2, 36, -140, 44);
    glow.addColorStop(0, 'rgb(170 215 255 / 35%)');
    glow.addColorStop(1, 'rgb(170 215 255 / 0%)');
    ctx.fillStyle = glow;
    ctx.fillRect(-8, -184, 88, 88);
  }
  ctx.restore();
}

/**
 * The cat that is in every one of the paintings: sitting by the kerb with its tail
 * going — or, once the search has run long, curled up asleep under the bench's end
 * with its z's going up.
 */
function cat(ctx: CanvasRenderingContext2D, p: Palette, t: number, asleep: boolean): void {
  ctx.save();
  if (asleep) {
    ctx.translate(548, FEET + 2);
    const breath = 1 + Math.sin(t * 1.8) * 0.04;
    ctx.fillStyle = p.cat;
    ctx.beginPath();
    ctx.ellipse(0, -11, 24, 12 * breath, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(-18, -12, 10, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(-26, -15);
    ctx.lineTo(-27, -27);
    ctx.lineTo(-19, -20);
    ctx.moveTo(-15, -21);
    ctx.lineTo(-9, -29);
    ctx.lineTo(-9, -17);
    ctx.fill();
    // The tail round the front, and a closed eye.
    ctx.strokeStyle = p.cat;
    ctx.lineWidth = 5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(22, -6);
    ctx.quadraticCurveTo(14, 4, -8, 0);
    ctx.stroke();
    ctx.strokeStyle = p.shoe;
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.moveTo(-24, -12);
    ctx.quadraticCurveTo(-21, -10, -18, -12);
    ctx.stroke();
    // Three z's, each rising and fading in turn.
    for (let i = 0; i < 3; i++) {
      const into = (t / 2.4 + i / 3) % 1;
      const size = 5 + into * 5;
      const zx = -20 + into * 18;
      const zy = -32 - into * 46;
      ctx.strokeStyle = `rgb(255 255 255 / ${Math.round(Math.sin(into * Math.PI) * 90)}%)`;
      ctx.lineWidth = 2.2;
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(zx, zy);
      ctx.lineTo(zx + size, zy);
      ctx.lineTo(zx, zy + size);
      ctx.lineTo(zx + size, zy + size);
      ctx.stroke();
    }
    ctx.restore();
    return;
  }
  ctx.translate(700, FEET + 2);
  const swish = Math.sin(t * 2.2) * 0.35;
  ctx.strokeStyle = p.cat;
  ctx.lineWidth = 5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(10, -6);
  ctx.quadraticCurveTo(30, -4 + swish * 8, 30 + swish * 10, -22 - swish * 6);
  ctx.stroke();
  ctx.fillStyle = p.cat;
  ctx.beginPath();
  ctx.ellipse(0, -16, 14, 17, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(-4, -38, 10, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-13, -42);
  ctx.lineTo(-11, -55);
  ctx.lineTo(-5, -46);
  ctx.moveTo(1, -46);
  ctx.lineTo(5, -55);
  ctx.lineTo(6, -41);
  ctx.fill();
  ctx.fillStyle = p.catLight;
  ctx.beginPath();
  ctx.ellipse(-5, -16, 6, 11, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * The cat on the bench in the last minute, looking up the road with its tail flicking
 * over the seat's edge: its head lifts as if at a car, an ear twitches, and once every
 * fourteen seconds it looks round the other way — less often than the passenger looks
 * down at the phone. The head turns as the passenger's does, never quite edge-on.
 */
function watchingCat(ctx: CanvasRenderingContext2D, p: Palette, t: number): void {
  const swish = Math.sin(t * 3.1) * 0.5;
  const peer = Math.max(0, Math.sin(t * 0.9)) * 3;
  const twitch = t % 3.4 < 0.18 ? -0.35 : 0;
  const turn = Math.cos(glance(t, 14, 9.5) * Math.PI);
  ctx.save();
  ctx.translate(BENCH + 62, FEET - 50);
  ctx.strokeStyle = p.cat;
  ctx.lineWidth = 5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(10, -4);
  ctx.quadraticCurveTo(26, 2, 24 + swish * 8, 20 + Math.abs(swish) * 4);
  ctx.stroke();
  ctx.fillStyle = p.cat;
  ctx.beginPath();
  ctx.ellipse(0, -16, 14, 17, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.save();
  ctx.translate(-6, -38 - peer);
  ctx.scale((turn < 0 ? -1 : 1) * Math.max(Math.abs(turn), 0.3), 1);
  ctx.beginPath();
  circle(ctx, 0, 0, 10);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(-9, -4);
  ctx.lineTo(-8 + twitch * 6, -18);
  ctx.lineTo(-1, -9);
  ctx.moveTo(4, -8);
  ctx.lineTo(8, -18);
  ctx.lineTo(9, -3);
  ctx.fill();
  ctx.fillStyle = p.shoe;
  ctx.beginPath();
  circle(ctx, -6, -1, 1.6);
  ctx.fill();
  ctx.restore();
  ctx.fillStyle = p.catLight;
  ctx.beginPath();
  ctx.ellipse(-5, -16, 6, 11, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/**
 * The pin from the first stage, over the passenger's head in the last minute: amber,
 * as the words over it are, bobbing a little, and the ring round the mark emptying as
 * the minute runs out. Full where nothing will give up, so that the pin never says
 * more than the panel does.
 */
function overhead(ctx: CanvasRenderingContext2D, who: Pose, t: number, left: number | null): void {
  const share = left === null ? 1 : Math.min(1, Math.max(0, left / MINUTE));
  ctx.save();
  ctx.translate(who.x + 6, FEET - 214 + who.bob + Math.sin(t * 1.6) * 4);
  ctx.scale(1.55, 1.55);
  ctx.rotate(Math.sin(t * 1.3) * 0.04);
  ctx.fillStyle = AMBER;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.bezierCurveTo(-6, -14, -20, -22, -20, -38);
  ctx.arc(0, -38, 20, Math.PI, 0);
  ctx.bezierCurveTo(20, -22, 6, -14, 0, 0);
  ctx.fill();
  ctx.fillStyle = '#FFFFFF';
  ctx.beginPath();
  circle(ctx, 0, -38, 12.5);
  ctx.fill();
  mark(ctx, AMBER_DEEP, 0, -38, 18, false);
  ctx.lineWidth = 2.6;
  ctx.lineCap = 'round';
  ctx.strokeStyle = 'rgb(255 255 255 / 40%)';
  ctx.beginPath();
  ctx.arc(0, -38, 16, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = '#FFFFFF';
  ctx.beginPath();
  ctx.arc(0, -38, 16, -Math.PI / 2, -Math.PI / 2 + Math.max(0.001, share) * Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** The bushes at the foot of the picture, either side, as the paintings frame theirs. */
function bushes(ctx: CanvasRenderingContext2D, p: Palette, t: number): void {
  const clumps: [number, number, number, 0 | 1 | 2][] = [
    [-20, 580, 90, 0],
    [60, 592, 70, 1],
    [120, 610, 50, 2],
    [960, 588, 80, 0],
    [1040, 570, 90, 1],
    [900, 612, 50, 2],
  ];
  for (const [x, y, r, shade] of clumps) {
    const sway = Math.sin(t * 0.9 + x * 0.02) * 1.5;
    ctx.fillStyle = p.bushes[shade];
    ctx.beginPath();
    circle(ctx, x + sway, y, r);
    circle(ctx, x + r * 0.6 + sway, y + r * 0.2, r * 0.7);
    ctx.fill();
  }
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
}

/**
 * A whole circle as a path of its own, so that several make one shape without the
 * lines `arc` would otherwise draw from one to the next.
 */
function circle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number): void {
  ctx.moveTo(x + r, y);
  ctx.arc(x, y, r, 0, Math.PI * 2);
}
